import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { Pagination } from "../general/pagination.component";
import React from "react";
import {
  MonthSalaryPageContainer,
  MonthSalaryBody,
  MonthSalaryCell,
  FirstContainer,
  SecondContainer,
  ThirdContainer,
  FourthContainer,
  FifthContainer,
  SixthContainer,
  DividerLine1,
  DividerLine2,
  AGICell,
  SeventhContainer,
  LogoContainer,
  EmptyDiv,
  Logo,
} from "./salary.styled";
import { Text } from "components/Typography/text.styled";
import { FontSize, FontFamily } from "config/font";
import { Color } from "config/colors";
import { CustomIcon } from "components/icon/customIcon.component";
import { DetailsState } from "./agiList.component";
import { IconCircularContainer } from "../shared/actionIcons.styled";
import { UpdateWorkerSalaryModal } from "./updateWorkerSalaryModal.component";
import { useMonthSalaryStore } from "web-apps/company/stores/monthSalaryStore/monthSalaryStore";
import { FuturePastSalaryRow } from "./futurePastSalaryRow.component";
import { OrgWorker } from "model/OrganizationalStructure";
import { WorkerProfile } from "../organizationalStructure/workerProfile.component";
import { PaymentMethod } from "model/Gig";
import SimflexityPayLogo from "images/logo/svg/simflexityPay-logo.svg";

type Props = {
  selectedWorker?: number;
};

export const SalaryList: React.FC<Props> = ({ selectedWorker }) => {
  const { t } = useTranslation();
  const [{ company }] = useCompanyStore();
  const [state, monthSalaryActions] = useMonthSalaryStore();

  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [displayedWorkers, setDisplayedWorkers] = useState<OrgWorker[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [detailsState, setDetailsState] = useState<DetailsState>({
    isOpen: false,
    selectedIndex: null,
  });

  const [clickedWorker, setClickedWorker] = useState<number | undefined>(
    undefined
  );
  const [showWorkerProfile, setShowWorkerProfile] = useState(false);
  const [showEditWorkerSalaryModal, setShowEditWorkerSalaryModal] =
    useState(false);
  const [selectedWorkerWithSalary, setSelectedWorkerWithSalary] =
    useState<OrgWorker>();

  const itemsPerPage = 25;
  const fetchAmount = 50;

  const fetchAllSalaries = async () => {
    if (!company?.id || isLoading) return;

    setIsLoading(true);

    try {
      const first = await monthSalaryActions.getAllSalaries(
        company.id,
        0,
        fetchAmount
      );
      const total = state.total || first?.data?.total || 0;
      const totalPagesToFetch = Math.ceil(total / fetchAmount);

      for (let i = 1; i < totalPagesToFetch; i++) {
        await monthSalaryActions.getAllSalaries(company.id, i, fetchAmount);
        await new Promise((r) => setTimeout(r, 100));
      }
    } catch (err) {
      console.error("Failed to fetch salaries", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (company?.id) {
      setPage(0);
      monthSalaryActions.clearSalaries();
      fetchAllSalaries();
    }
  }, [company?.id]);

  useEffect(() => {
    const all = selectedWorker
      ? state.workersWithSalary.filter((w) => w.workerId === selectedWorker)
      : state.workersWithSalary;

    const totalPageCount = Math.ceil(all.length / itemsPerPage);
    setTotalPages(totalPageCount);

    const start = page * itemsPerPage;
    const end = start + itemsPerPage;

    setDisplayedWorkers(all.slice(start, end));
  }, [state.workersWithSalary, selectedWorker, page]);

  const toggleDetails = useCallback((index: number) => {
    setDetailsState((prev) => ({
      selectedIndex: index,
      isOpen: prev.selectedIndex === index ? !prev.isOpen : true,
    }));
  }, []);

  const isDetailsVisible = useCallback(
    (index: number) =>
      detailsState.isOpen && detailsState.selectedIndex === index,
    [detailsState]
  );

  const handleClickRow = (worker: OrgWorker, index: number) => {
    if (worker.employmentInfo.next || worker.employmentInfo.previous) {
      toggleDetails(index);
    }
  };

  const renderSalaries = () => {
    if (displayedWorkers.length === 0) {
      return (
        <Text style={{ textAlign: "center", marginTop: 70 }}>
          {t("Salary.NoSalaries")}
        </Text>
      );
    }

    return (
      <MonthSalaryBody>
        {displayedWorkers.map((salary, index) => {
          const detailsOpen = isDetailsVisible(index);
          const currency = "kr";
          const isTrainee =
            salary.employmentInfo.current.employmentType == "Trainee";
          const isSimflexityEmployee =
            salary.employmentInfo.current.employmentType ===
            "SimflexityEmployee";

          return (
            <React.Fragment key={`worker-${salary.workerId}`}>
              <MonthSalaryCell isInactive={!salary.isActive}>
                <FirstContainer
                  onClick={() => {
                    setClickedWorker(salary.workerId);
                    setShowWorkerProfile(true);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <Text fontSize={FontSize.Small} color={Color.SeaBlue600}>
                    {t("General.Giggers")}
                  </Text>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <Text
                      color={Color.MidnightBlue}
                      fontFamily={FontFamily.MontserratRegular}
                    >
                      {`${salary.firstName} ${salary.lastName} ${
                        !salary.isActive ? t("Salary.Inactive") : ""
                      }`}
                    </Text>
                    {salary.employmentInfo.current.paymentType ===
                    PaymentMethod.SIMFLEXITY_PAY ? (
                      <LogoContainer>
                        <Logo
                          src={SimflexityPayLogo}
                          alt="SimflexityPay logo"
                        />
                      </LogoContainer>
                    ) : (
                      <EmptyDiv />
                    )}
                  </div>
                </FirstContainer>
                <DividerLine1 onClick={() => handleClickRow(salary, index)} />
                <SecondContainer onClick={() => handleClickRow(salary, index)}>
                  <Text fontSize={FontSize.Small} color={Color.SeaBlue600}>
                    {isTrainee
                      ? t("Salary.Salary")
                      : t(`Salary.${salary.employmentInfo.current.salaryType}`)}
                  </Text>
                  <Text color={Color.MidnightBlue}>
                    {isTrainee
                      ? "-"
                      : `${salary.employmentInfo.current.salary} ${currency}`}
                  </Text>
                </SecondContainer>
                <ThirdContainer onClick={() => handleClickRow(salary, index)}>
                  <Text fontSize={FontSize.Small} color={Color.SeaBlue600}>
                    {t("Salary.WorkPercentage")}
                  </Text>
                  <Text color={Color.MidnightBlue}>
                    {isTrainee
                      ? "-"
                      : salary.employmentInfo.current.salaryType === "Monthly"
                      ? `${salary.employmentInfo.current.workPercentage * 100}%`
                      : t("Salary.Hours")}
                  </Text>
                </ThirdContainer>
                <FourthContainer>
                  <Text fontSize={FontSize.Small} color={Color.SeaBlue600}>
                    {t("OrganizationalStructure.TaxTable")}
                  </Text>
                  <Text color={Color.MidnightBlue}>
                    {isTrainee || isSimflexityEmployee
                      ? "-"
                      : salary.employmentInfo.current.taxTable}
                  </Text>
                </FourthContainer>
                <FifthContainer>
                  <Text fontSize={FontSize.Small} color={Color.SeaBlue600}>
                    {t("OrganizationalStructure.EmploymentType")}
                  </Text>
                  <Text color={Color.MidnightBlue}>
                    {t(
                      `OrganizationalStructure.${salary.employmentInfo.current.employmentType}`
                    )}
                  </Text>
                </FifthContainer>
                <SixthContainer>
                  <Text fontSize={FontSize.Small} color={Color.SeaBlue600}>
                    {t("Salary.ApplyFrom")}
                  </Text>
                  <Text color={Color.MidnightBlue}>
                    {`${salary.employmentInfo.current.startDate.split("T")[0]}${
                      !salary.isActive
                        ? ` - ${
                            salary.employmentInfo.current.endDate.split("T")[0]
                          }`
                        : ""
                    }`}
                  </Text>
                </SixthContainer>
                <DividerLine2 onClick={() => handleClickRow(salary, index)} />
                <SeventhContainer>
                  <IconCircularContainer
                    isAccept
                    onClick={() => {
                      setSelectedWorkerWithSalary(salary);
                      setShowEditWorkerSalaryModal(true);
                    }}
                    style={{ marginRight: 20 }}
                  >
                    <CustomIcon
                      name="settings"
                      size="20px"
                      padding="2px"
                      color={Color.White}
                    />
                  </IconCircularContainer>
                  {(salary.employmentInfo.previous ||
                    salary.employmentInfo.next) && (
                    <CustomIcon
                      name={detailsOpen ? "upward_arrow" : "downward_arrow"}
                      size="24px"
                      color={Color.BurntSienna}
                      onClick={() => handleClickRow(salary, index)}
                    />
                  )}
                </SeventhContainer>
              </MonthSalaryCell>
              {detailsOpen && (
                <AGICell key={`agi-${index}`}>
                  <FuturePastSalaryRow
                    futureSalary={salary.employmentInfo.next}
                    pastSalary={salary.employmentInfo.previous}
                  />
                </AGICell>
              )}
            </React.Fragment>
          );
        })}
      </MonthSalaryBody>
    );
  };

  return (
    <>
      <MonthSalaryPageContainer>
        {renderSalaries()}
        <Pagination page={page} setPage={setPage} totalPages={totalPages} />
      </MonthSalaryPageContainer>

      {selectedWorkerWithSalary && (
        <UpdateWorkerSalaryModal
          showUpdateWorkerSalaryModal={showEditWorkerSalaryModal}
          setShowUpdateWorkerSalaryModal={setShowEditWorkerSalaryModal}
          selectedWorkerWithSalary={selectedWorkerWithSalary}
        />
      )}
      {clickedWorker && (
        <WorkerProfile
          worker={clickedWorker}
          showWorkerProfile={showWorkerProfile}
          setShowWorkerProfile={setShowWorkerProfile}
          setClickedWorker={setClickedWorker}
        />
      )}
    </>
  );
};
